import { captureException } from '@sentry/vue'

/** Where in the Guide the failure happened (`ai.surface` tag). */
export type AiSurface =
  | 'llm-turn'
  | 'tool'
  | 'context'
  | 'persistence'
  | 'feedback'
  | 'prompt'

/**
 * Which side owns the failure (`ai.layer` tag): `toolkit` for the agent, LLM
 * stream, and `@onxmaps/ai-toolkit` internals; `host` for this app's wiring
 * (dependency adapters, layer/viewer bridges, storage).
 */
export type AiLayer = 'toolkit' | 'host'

export type AiErrorOptions = {
  surface: AiSurface
  /** Defaults to `toolkit` — the common case for a streamed turn or tool call. */
  layer?: AiLayer
  /** Tool name. Only meaningful when `surface` is `tool`. */
  tool?: string
  /**
   * Extra indexed tags for narrowing within a surface — most often `location`,
   * the call-site convention used across the rest of this app. Canonical tags
   * always win, so a caller can't accidentally break routing.
   */
  tags?: Record<string, string>
  /** Non-indexed diagnostic context (model, message count, origin, …). */
  extra?: Record<string, unknown>
}

/**
 * Reports an onX Guide error to Sentry under the canonical tag contract, so a
 * single set of alert rules can route every Guide failure regardless of which
 * app or surface it came from. Prefer this over calling `captureException`
 * directly anywhere under the AI module.
 */
export function reportAiError(error: unknown, options: AiErrorOptions): void {
  const { surface, layer = 'toolkit', tool, tags, extra } = options

  captureException(error, {
    tags: {
      ...tags,
      // Sentry alert rules filter on `feature:ai-guide` to funnel Guide bugs
      // to the AI team, and each app's `beforeSend` sampler allow-lists this
      // value so Guide errors bypass the 1% production sample rate. Changing
      // it means updating both.
      feature: 'ai-guide',
      'ai.app': 'fish',
      'ai.layer': layer,
      'ai.surface': surface,
      ...(tool ? { 'ai.tool': tool } : {}),
    },
    ...(extra ? { extra } : {}),
  })
}

/**
 * Tool failures that are expected control flow rather than defects: a turn the
 * user cancelled mid-flight, or the model calling a tool without the arguments
 * it needs (which the model recovers from on its own by asking a follow-up).
 * Reporting these would bury the real failures.
 */
const BENIGN_TOOL_ERROR_PATTERNS = [
  /\baborted\b/i,
  /\bcancelled\b/i,
  /must be provided/i,
  /unable to determine a location to search near/i,
]

/** True when a tool error is expected control flow and should not be reported. */
export function isBenignToolError(error: Error): boolean {
  return BENIGN_TOOL_ERROR_PATTERNS.some((pattern) =>
    pattern.test(error.message)
  )
}
